import { createContext, useContext } from 'react';
import type { Session } from '@supabase/supabase-js';
import type { AccessProfile } from '@/features/access/types';

export type AuthStatus = 'unconfigured' | 'loading' | 'signed_out' | 'signed_in';

export interface SignUpInput {
  fullName: string;
  mobileE164: string;
  email: string;
  password: string;
  preferredLanguage: 'ar' | 'en';
}

export interface AuthValue {
  status: AuthStatus;
  session: Session | null;
  access: AccessProfile | null;
  signIn: (email: string, password: string) => Promise<{ error: null | 'invalid' | 'unverified' | 'rate_limited' | 'unconfigured' }>;
  signUp: (input: SignUpInput) => Promise<{ error: null | 'exists' | 'failed' | 'unconfigured'; needsVerification: boolean }>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ error: null | 'rate_limited' | 'unconfigured' }>;
  /** Re-sends the sign-up verification email. Never reveals whether the account exists. */
  resendVerification: (email: string) => Promise<{ error: null | 'rate_limited' | 'unconfigured' }>;
  updatePassword: (
    password: string,
  ) => Promise<{ error: null | 'reauthenticate' | 'same_password' | 'failed' | 'unconfigured' }>;
  refreshAccess: () => Promise<AccessProfile | null>;
}

export const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
